import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconEdit, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadListCsv } from '../utils/csv';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/CustomerInvoices.jsx. `status`
// is not list-item-backed (CustomerInvoiceController::validated() has no
// `status` rule at all — see CustomerInvoiceDetail.tsx's note), so
// STATUS_LABELS stays a hardcoded map here, unlike quotation_status.
const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  issued: 'Issued',
  sent: 'Sent',
  partially_paid: 'Partially Paid',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
};

const TYPE_LABELS: Record<string, string> = {
  general: 'General',
};

function statusBadgeClass(status: string): string {
  if (status === 'paid') return 'badge-normal';
  if (['overdue', 'cancelled'].includes(status)) return 'badge-important';
  return 'badge-neutral';
}

interface InvoiceRow {
  id: number;
  invoice_number: string;
  type: string;
  customer_name: string;
  amount: number;
  paid_total: number | null;
  due_date: string | null;
  status: string;
}

export function CustomerInvoices() {
  const { settings } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const filters = useMemo(
    () => ({ search: search || undefined, status: status || undefined }),
    [search, status]
  );
  const { data, loading, error, setPage, reload: load } = usePaginatedList<InvoiceRow>('/customer-invoices', filters);

  const remove = async (inv: InvoiceRow) => {
    if (!(await confirmDelete(`Delete invoice ${inv.invoice_number}?`))) return;
    try {
      await api.delete(`/customer-invoices/${inv.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<InvoiceRow>(
      api,
      '/customer-invoices',
      { search: search || undefined, status: status || undefined },
      [
        { label: 'Number', value: (r) => r.invoice_number },
        { label: 'Type', value: (r) => TYPE_LABELS[r.type] ?? r.type },
        { label: 'Customer', value: (r) => r.customer_name },
        { label: 'Amount', value: (r) => r.amount },
        { label: 'Paid', value: (r) => r.paid_total ?? 0 },
        { label: 'Due', value: (r) => r.due_date ?? '' },
        { label: 'Status', value: (r) => STATUS_LABELS[r.status] ?? r.status },
      ],
      'invoices.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Invoices</h1>
        <Link className="btn btn-primary" to="/revenue/invoices/new">
          + Add invoice
        </Link>
      </header>
      <p className="muted">Invoices billed to customers, with payments tracked via receipts.</p>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search number or customer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Number</th>
                <th>Type</th>
                <th>Customer</th>
                <th className="right">Amount</th>
                <th className="right">Paid</th>
                <th>Due</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((inv) => (
                  <tr key={inv.id}>
                    <td className="muted">{inv.invoice_number}</td>
                    <td className="muted">{TYPE_LABELS[inv.type] ?? inv.type}</td>
                    <td>{inv.customer_name}</td>
                    <td className="right">{fmtMoney(inv.amount)}</td>
                    <td className="right">{fmtMoney(inv.paid_total ?? 0)}</td>
                    <td>{inv.due_date ?? '—'}</td>
                    <td>
                      <span className={`badge ${statusBadgeClass(inv.status)}`}>
                        {STATUS_LABELS[inv.status] ?? inv.status}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/revenue/invoices/${inv.id}`} />
                      <IconButton icon={IconEdit} label="Edit" to={`/revenue/invoices/${inv.id}?edit=1`} />
                      <IconButton
                        icon={IconTrash}
                        label="Delete"
                        variant="danger-ghost"
                        onClick={() => remove(inv)}
                        hidden={inv.status !== 'draft'}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="muted center">
                    No invoices found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
